import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Image as ImageIcon, Link2, X } from "lucide-react";
import api from "../../lib/api";
import { useI18n } from "../../lib/i18n";

const TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

export default function Composer({ conv, onSent }) {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [imgB64, setImgB64] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [sending, setSending] = useState(false);

  const takeFile = (f) => {
    if (!TYPES.includes(f.type)) { toast.error("Format tidak didukung (JPG, PNG, WEBP)"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const r = new FileReader(); r.onload = () => setImgB64(r.result); r.readAsDataURL(f);
  };
  const onPick = (e) => { const f = e.target.files[0]; e.target.value = ""; if (f) takeFile(f); };
  const onPaste = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find((i) => i.kind === "file" && i.type.startsWith("image/"));
    if (!item) return;
    e.preventDefault();
    takeFile(item.getAsFile());
  };

  const send = async (e) => {
    e?.preventDefault();
    if (sending || (!text.trim() && !imgB64 && !videoUrl.trim())) return;
    setSending(true);
    try {
      const payload = { conversation_id: conv.id };
      if (text.trim()) payload.text = text.trim();
      if (imgB64) payload.image_b64 = imgB64;
      if (videoUrl.trim()) {
        if (!/^https?:\/\/\S+$/i.test(videoUrl.trim())) { toast.error("URL video harus diawali https://"); setSending(false); return; }
        payload.video_url = videoUrl.trim();
      }
      await api.post("/messages", payload);
      setText(""); setImgB64(null); setVideoUrl(""); setShowVideo(false);
      onSent();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal kirim"); } finally { setSending(false); }
  };

  return (
    <form onSubmit={send} className="border-t border-[#35353B] p-3">
      {imgB64 && (
        <div data-testid="chat-image-preview" className="mb-2 inline-flex items-start gap-2 bg-[#202127] border border-[#35353B] rounded-sm p-2">
          <img src={imgB64} alt="preview" className="h-24 max-w-[220px] object-contain rounded-sm" />
          <div className="text-[10px] text-[#A8A8AD] w-24">{t("chat.image_ready")}</div>
          <button data-testid="chat-image-remove-btn" type="button" onClick={() => setImgB64(null)} className="bg-[#F06464] text-white rounded-full p-0.5"><X size={12} /></button>
        </div>
      )}
      {showVideo && (
        <input data-testid="chat-video-url-input" placeholder={t("chat.video_url")} value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
          className="mb-2 w-full bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] rounded-sm px-3 py-2 outline-none text-sm" />
      )}
      <div className="flex items-center gap-2">
        <label className="cursor-pointer p-2 text-[#A8A8AD] hover:text-[#D4AF37] transition-colors" title="Upload">
          <ImageIcon size={18} />
          <input data-testid="chat-image-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="hidden" />
        </label>
        <button data-testid="chat-video-toggle-btn" type="button" title={t("chat.video_url")} onClick={() => setShowVideo(!showVideo)} className={`p-2 transition-colors ${showVideo ? "text-[#D4AF37]" : "text-[#A8A8AD] hover:text-[#D4AF37]"}`}><Link2 size={18} /></button>
        <textarea data-testid="chat-text-input" value={text} onChange={(e) => setText(e.target.value)} onPaste={onPaste}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t("chat.type_message_paste")} rows={1}
          className="flex-1 bg-[#191A1F] border border-[#35353B] focus:border-[#D4AF37] text-[#F5F3ED] rounded-sm px-3 py-2 outline-none text-sm resize-none transition-colors" />
        <button data-testid="chat-send-btn" type="submit" disabled={sending} className="bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] disabled:opacity-50 rounded-sm p-2.5 transition-colors"><Send size={16} /></button>
      </div>
    </form>
  );
}
